import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { matchPath } from 'react-router-dom';
import { TranslationKey } from '../i18n/translationKeys';
import { IGNORED_RELANCES_ROUTE, UPCOMING_RELANCES_ROUTE } from '../routes/paths';

export const ROUTE_LABELS: Array<{ pattern: string; labelKey: string }> = [
    { pattern: '/customer-follow-up/:id', labelKey: TranslationKey.CustomerFollowUp.Title },
    { pattern: '/history', labelKey: TranslationKey.History.Title },
    { pattern: '/settings', labelKey: TranslationKey.Settings.Title },
    { pattern: '/acomba', labelKey: TranslationKey.AcombaBridge.PageName },
    { pattern: IGNORED_RELANCES_ROUTE, labelKey: TranslationKey.IgnoredRelances.Title },
    { pattern: UPCOMING_RELANCES_ROUTE, labelKey: TranslationKey.UpcomingRelances.Title },
    { pattern: '/', labelKey: TranslationKey.Header.HomeName },
];

export interface StackEntry {
    path: string;      // full path + search (e.g. '/history?customerId=7')
    labelKey: string;  // i18n key for the back button label
    pattern: string;   // route pattern for deduplication (e.g. '/history')
    state?: Record<string, unknown>;  // location.state to restore on back navigation
}

export function buildStackEntry(pathname: string, search: string, state?: Record<string, unknown>): StackEntry {
    for (const { pattern, labelKey } of ROUTE_LABELS) {
        if (matchPath(pattern, pathname)) {
            return { path: pathname + search, labelKey, pattern, state };
        }
    }
    return { path: pathname + search, labelKey: TranslationKey.Header.HomeName, pattern: pathname, state };
}

interface NavigationStackState {
    stack: StackEntry[];
    push: (entry: StackEntry) => void;
    pop: () => StackEntry | undefined;
    peek: () => StackEntry | undefined;
    clear: () => void;
}

export const useNavigationStackStore = create<NavigationStackState>()(
    persist(
        (set, get) => ({
            stack: [],
            push: (entry) =>
                set((state) => ({
                    stack: [
                        ...state.stack.filter((e) => e.pattern !== entry.pattern),
                        entry,
                    ],
                })),
            pop: () => {
                const { stack } = get();
                const top = stack[stack.length - 1];
                set({ stack: stack.slice(0, -1) });
                return top;
            },
            peek: () => {
                const { stack } = get();
                return stack[stack.length - 1];
            },
            clear: () => set({ stack: [] }),
        }),
        {
            name: 'navigation-stack',
            storage: createJSONStorage(() => sessionStorage),
        }
    )
);
